import type { MorphemeInfoPanelCompositionCompoundsListViewGate } from "#get-morpheme-info-panel-composition-compounds-list-view-gate";
import { MorphemeDetailsTileList } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.details.list";
import { MorphemeGlyphTileList } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { filterCardsHidingAuthoringCompleted } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.file.completion";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { getCardFrequencyRank } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.rank";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useCallback, useMemo } from "react";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { hasLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";

const COMPOUNDS_HREF = "/(nav)/(app)/browse/morpheme/[morpheme]";

/**
 * The Compounds body. Only `getMorphemeInfoPanelCompositionCompoundsListViewGate` decides whether it
 * renders. Compact (the default) is a grid of glyph tiles; `isExpanded` shows the full detail tiles.
 */
export function MorphemeInfoPanelCompositionCompoundsListView({
  gate,
  isExpanded,
  onRevealMore,
  onShowRareCompounds,
  rareCompoundCount,
}: {
  gate: MorphemeInfoPanelCompositionCompoundsListViewGate;
  isExpanded: boolean;
  onRevealMore: () => void;
  /** Set while rarer compounds are hidden from the list (the header filter's "Show rare compounds"). */
  onShowRareCompounds: (() => void) | undefined;
  /** Compounds outside the browsing corpus. */
  rareCompoundCount: number;
}) {
  const { cards, compoundCharacterList, hasMore, isCardsPending } = gate;
  const { hanziScript: script } = useActorUser();
  const [hideCompletedAuthoringCardsEnabled] = useDeviceSetting(
    "hideCompletedAuthoringCardsEnabled",
  );

  const compoundCardList = useMemo(() => {
    const filtered = filterCardsHidingAuthoringCompleted(
      cards,
      Boolean(hideCompletedAuthoringCardsEnabled),
    );
    return [...filtered].sort((a, b) => {
      const aRank = getCardFrequencyRank(a, script) ?? Number.POSITIVE_INFINITY;
      const bRank = getCardFrequencyRank(b, script) ?? Number.POSITIVE_INFINITY;
      return aRank - bRank;
    });
  }, [cards, hideCompletedAuthoringCardsEnabled, script]);
  const compoundGlyphList = useMemo(
    () => compoundCardList.map((card) => card.front),
    [compoundCardList],
  );
  // Same destination as a detail card's tap, so both views open a compound identically.
  const pushMutate = useBrowseMorphemePageNavigator().push.mutate;
  const handleGlyphNavigate = useCallback(
    (morpheme: string) => {
      pushMutate({ morpheme, params: { view: "mnemonics" } });
    },
    [pushMutate],
  );

  if (hasLength(compoundCharacterList, 0)) {
    // Every compound is outside the browsing corpus: offer them rather than claiming there are none.
    if (onShowRareCompounds !== undefined) {
      return (
        <View className="items-start gap-2 px-3 py-2">
          <Text className="text-[20px] text-muted-foreground italic">
            Only rarer characters use this character as a component.
          </Text>
          <Button
            onPress={onShowRareCompounds}
            size="sm"
            testID="compounds-show-rare-characters"
            variant="outline"
          >
            <Text>
              {`Show ${rareCompoundCount.toLocaleString()} rarer character${rareCompoundCount === 1 ? "" : "s"}`}
            </Text>
          </Button>
        </View>
      );
    }
    return (
      <View className="px-3 py-2">
        <Text className="text-[20px] text-muted-foreground italic">
          No compounds found that use this character as a component yet.
        </Text>
      </View>
    );
  }

  if (hideCompletedAuthoringCardsEnabled && hasLength(compoundCardList, 0) && isNonEmpty(cards)) {
    return (
      <View className="px-3 py-2">
        <Text className="text-[20px] text-muted-foreground italic">
          All compounds here are authoring-complete and hidden. Turn off &quot;Hide completed
          cards&quot; in Admin settings to show them.
        </Text>
      </View>
    );
  }

  if (hasLength(compoundCardList, 0)) {
    return (
      <View className="px-3 py-2">
        <Text className="text-[20px] text-muted-foreground italic">
          Could not load compound cards.
        </Text>
      </View>
    );
  }

  // Unrevealed compounds are idle, not loading. Keep paging reachable even if a short grid
  // doesn't produce a native scroll event; only a requested page gets a spinner.
  const moreBelowFooter = isCardsPending ? (
    <View className="items-center py-3">
      <ActivityIndicator accessibilityLabel="Loading more compounds" size="small" />
    </View>
  ) : hasMore ? (
    <View className="items-center py-3">
      <Button onPress={onRevealMore} size="sm" variant="outline">
        <Text>Load more compounds</Text>
      </Button>
    </View>
  ) : null;

  if (!isExpanded) {
    return (
      <View className="gap-1" testID="compounds-glyph-grid">
        <MorphemeGlyphTileList
          gap={4}
          graphemeList={compoundGlyphList}
          href={COMPOUNDS_HREF}
          onNavigate={handleGlyphNavigate}
        />
        {moreBelowFooter}
      </View>
    );
  }

  return (
    <View className="gap-1">
      <MorphemeDetailsTileList
        cards={compoundCardList}
        compact
        horizontalPadding={0}
        ListFooterComponent={moreBelowFooter}
        scrollable={false}
        verticalPadding={0}
      />
    </View>
  );
}
